/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import styled, { css } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { GatsbyImage } from 'gatsby-plugin-image';
import {
  Video,
  Heading as GrommetHeading,
  Paragraph as GrommetParagraph,
} from 'grommet';
import Link from './Link';

// Rich Text Styles

const GrommetTypeSizeToCSS = ({ size, height, maxWidth, letterSpacing }) => css`
  font-size: ${size};
  line-height: ${height};
  max-width: ${maxWidth};
  letter-spacing: ${letterSpacing};
`;

const GrommetTypeSizesToMediaQueries = ({ small, medium }) => css`
  ${GrommetTypeSizeToCSS(small)}
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    ${GrommetTypeSizeToCSS(medium)}
  }
`;

export const StyledRichText = styled.div`
  width: 100%;

  /* Contentful delivers complicated nested markup that was a hassle to try to reliably map to grommet components. So that all of these RichText components can be consistently styled, I'm dumping the grommet theme to CSS just for these to pick up with whatever markup they render. */

  /* HEADINGS */

  ${({ textColor }) => {
    switch (textColor) {
      case 'grey':
        return css`
          color: ${({ theme }) => theme.global.colors['light-2']};
        `;
      case 'dark':
        return css`
          color: ${({ theme }) => theme.global.colors['dark-3']};
        `;
      case 'black':
        return css`
          color: ${({ theme }) => theme.global.colors['black']};
        `;
      case 'white':
      default:
        return css`
          color: ${({ theme }) => theme.global.colors['light-1']};
        `;
    }
  }}

  ${({ theme }) =>
    Object.keys(theme.heading.level).map((k) => {
      const heading = theme.heading.level[k];
      return css`
        h${k} {
          font-family: ${heading.font.family};
          font-weight: ${heading.font.weight};

          ${GrommetTypeSizesToMediaQueries(heading)};
        }
      `;
    })}

  /* PARAGRAPHS */

  ${({ theme }) => {
    const { paragraph } = theme;
    return css`
      /* Headings */
      p {
        font-family: ${paragraph.font.family};
        font-weight: ${paragraph.font.weight};

        ${GrommetTypeSizesToMediaQueries(paragraph)};
      }
    `;
  }}

  a {
    text-decoration: none;
  }

  ul {
    list-style-position: outside;
    padding-left: 1em;
    p {
      display: inline;
    }
  }
  li + li {
    padding-top: 10px;
  }

  * + * {
    padding-top: 20px;
  }

  blockquote {
    > * {
      padding-left: 20px;
      border-left: solid 8px #d04a02;
      margin: 20px 0;
      color: #d04a02;
    }
  }
`;

// Rich Text Node Factories
const RichTextAsset = ({ data }) => {
  const {
    groups: { type: mediaType },
  } = data.target.file.contentType.match(/(?<type>.*)\/(?<format>.*)/);

  switch (mediaType) {
    case 'image':
      return (
        <GatsbyImage
          image={data.target.gatsbyImageData}
          alt={data.target.description}
          objectFit="contain"
          style={{ padding: '20px 0px' }}
        />
      );
    case 'video':
      return <Video src={data.target.file.url} />;
    default:
      return null;
  }
};

const RawHTMLEmbed = ({ data }) => {
  // Dangerously sets inner HTML from a provided snippet.
  function createMarkup() {
    return { __html: data.target.snippet.snippet };
  }

  /**
   * Nothing rendered out of here will be mapped to components,
   * so any text inside the embed will be unstyled.
   * Use the WYSIWYG editor for styled text, and this for embedding iframes.
   */

  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={createMarkup()} />;
};

const EmbedlyScript = () => (
  <Helmet>
    <script async src="//cdn.embedly.com/widgets/platform.js" charSet="UTF-8" />
  </Helmet>
);

const MarkdownEmbed = ({ data }) => {
  // If a Markdown Embed block is found, we render it with ReactMarkdown.
  // This library will parse HTML as well as markdown, as the Contentful
  // markdown editor will allow HTML. Furthermore, the embedly tool drops
  // HTML in here, so we need to be able to parse that too.

  /**
   *  I don't expect this markdown embed will be used for text content that
   *  much given it's embedded in a more  powerful WYSIWYG editor
   *  but I've built out support for headers, links, and paragraphs.
   *
   *  Please use the WYSIWYG editor for any other text types,
   *  or extend this below.
   */

  const markdown = data.target.snippet.snippet;
  return (
    <>
      <EmbedlyScript />
      <ReactMarkdown
        linkTarget={(url) => (url.includes('//') ? '_blank' : '')}
        // eslint-disable-next-line react/no-children-prop
        renderers={{
          paragraph: ({ children }) => (
            <GrommetParagraph>{children}</GrommetParagraph>
          ),
          heading: ({ children, level }) => (
            <GrommetHeading level={level}>{children}</GrommetHeading>
          ),
          link: ({ children, title, target, href }) => (
            <Link title={title} href={href} target={target}>
              {children}
            </Link>
          ),
        }}
        // eslint-disable-next-line react/no-children-prop
        children={markdown}
        allowDangerousHtml
      />
    </>
  );
};

const EmbeddedEntry = ({ data }) => {
  // eslint-disable-next-line no-underscore-dangle
  const type = data.target.__typename;
  switch (type) {
    case 'ContentfulEmbeddedHtml':
      return <RawHTMLEmbed data={data} />;
    case 'ContentfulEmbeddedMarkdown':
      return <MarkdownEmbed data={data} />;
    default:
      console.error('Unsupported Embed', type);
      return null;
  }
};

const RichTextLink = (node) => (
  <Link href={node.data.uri}>{node.content[0].value}</Link>
);

const Blockquote = (node) => (
  <blockquote>
    <GrommetParagraph>
      {node.content[0].content[0].value}
    </GrommetParagraph>
  </blockquote>
);

// Options
const RichTextOptions = {
  renderNode: {
    [BLOCKS.QUOTE]: (node) => Blockquote(node),
    [BLOCKS.EMBEDDED_ASSET]: (node) => RichTextAsset(node),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => EmbeddedEntry(node),
    [INLINES.HYPERLINK]: (node) => RichTextLink(node),
  },
};

const RichText = ({ richText, textColor, className }) => (
  /** * Accepts the entire object (raw and references) for a rich text field
   * as provided by Contentful and breaks it down into real HTML. */

  <StyledRichText textColor={textColor} className={className}>
    {renderRichText(richText, RichTextOptions)}
  </StyledRichText>
);
RichText.propTypes = {
  textColor: PropTypes.oneOf(['grey', 'dark', 'black', 'white']),
  richText: PropTypes.shape({
    raw: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    references: PropTypes.array,
  }).isRequired,
};

RichText.defaultProps = {
  textColor: 'black',
};

export default RichText;
